import { Injectable } from "@angular/core";
import { Action, Selector, State, StateContext } from "@ngxs/store";
import { Store } from "@ngxs/store";
import { PROMPT_ERROR } from "@store/exception/error.action";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { CustomerManagementModel } from "src/app/shared/interfaces/customer-management";

import { SET_CONCURRENT_LOGIN_ERROR } from "../login/login.action";
import { NAVIGATE_LOGIN_PAGE } from "../user/user.action";
import {
  ARCHIVE_CROSS_SELL,
  CLEAR_PRODUCT_PDF,
  COMPILE_GET_PDF_FOLDER,
  CREATE_CROSS_SELL,
  DOWNLOAD_REPORT,
  GET_ADMIN_CROSS_SELL_LIST,
  GET_ALIM_CP_DETAILS,
  GET_ALPHA_FLAG,
  GET_ALPHA_ROUTING_URL,
  GET_COMPANY_PROFILE_FOLDER,
  GET_COVER_NOTE_VALIDATION,
  GET_CROSS_SELL_ENQUIRY,
  GET_CUSTOMER_CLAIM_ENQUIRY,
  GET_CUSTOMER_DETAILS,
  GET_CUSTOMER_ENDORSEMENT_HISTORY,
  GET_CUSTOMER_INTERACTION_DATA,
  GET_CUSTOMER_MANAGEMENT_LOV,
  GET_CUSTOMER_POLICY_DETAILS,
  GET_CUSTOMER_POLICY_LIST,
  GET_ENQUIRY_LIST,
  GET_FORMS_FOLDER,
  GET_GUIDES_FOLDER,
  GET_GWP_DATA,
  GET_POLICY_VALIDATION,
  GET_POSTCODE_DETAILS,
  GET_PRODUCT_PDF,
  GET_REDIRECT_URL,
  PATCH_AS_OF_DATE,
  RESET_INTERACTION_DATA,
  SAVE_EMAIL_SENT_LOG,
  SAVE_MESSAGE_SENT_LOG,
  SET_ENQUIRY_SEARCH_CRITERIA,
  SET_GWP_DATA,
  SET_MAIN_AGENT,
  SET_POLICY_REQUEST,
  SET_UPDATE_CROSS_SELL_DETAILS,
  UPDATE_CROSS_SELL,
  UPDATE_CUSTOMER_DETAILS,
} from "./customer-management.action";
import { CustomerManagementService } from "./customer-management.service";

interface CrossSellMutationResponse {
  success?: boolean | null;
  status?: string | boolean | null;
  error?: string | null;
  message?: string | null;
  crossSellId?: number | null;
}

@State<CustomerManagementModel>({
  name: "CustomerManagementState",
  defaults: {
    enquirySearchCriteria: null,
    adminCrossSellList: null,
    lov: null,
    enquiryList: null,
    customerDetails: null,
    gwpData: null,
    updateCrossSellStatus: null,
    createCrossSellStatus: null,
    updateCrossSell: null,
    crossSellEnquiryList: null,
    crossSellReport: null,
    folderContents: null,
    companyProfileFolder: null,
    formsFolder: null,
    guidesFolder: null,
    customerPolicyList: null,
    asOfDate: null,
    policyRequest: null,
    postcodeDetails: null,
    updateCustomerStatus: null,
    customerEndorsementHistory: null,
    reportFilename: null,
    customerClaimDetails: null,
    customerInteractionData: null,
    customerPolicyDetails: null,
    mainAgentInd: null,
    fileName: null,
    isAlphaProduct: null,
    statusCode: null,
    coverNoteValidation: null,
    policyValidation: null,
    alimCustomerDetails: null,
  },
})
@Injectable()
export class CustomerManagementState {
  constructor(
    private readonly cmService: CustomerManagementService,
    private readonly store: Store,
  ) {}

  @Selector()
  public static getInteractionData(state: CustomerManagementModel) {
    return state.customerInteractionData ? state.customerInteractionData : null;
  }

  @Selector()
  public static getAsOfDate(state: CustomerManagementModel) {
    return state.asOfDate ? state.asOfDate : null;
  }

  @Selector()
  public static getCustomerManagementLov(state: CustomerManagementModel) {
    return state.lov ? state.lov : null;
  }

  @Selector()
  public static getGWPData(state: CustomerManagementModel) {
    return state.gwpData ? state.gwpData : null;
  }

  @Selector()
  public static getAdminCrossSellList(state: CustomerManagementModel) {
    return state.adminCrossSellList ? state.adminCrossSellList : null;
  }

  @Selector()
  public static getCrossSellEnquiryList(state: CustomerManagementModel) {
    return state.crossSellEnquiryList ? state.crossSellEnquiryList : null;
  }

  @Selector()
  public static getPostCode(state: CustomerManagementModel) {
    return state.lov.CLIENTPOSTCODELIST ? state.lov.CLIENTPOSTCODELIST : null;
  }

  @Selector()
  public static getCustomerEndtHistory(state: CustomerManagementModel) {
    return state.customerEndorsementHistory
      ? state.customerEndorsementHistory
      : null;
  }

  @Selector()
  public static getCustomerPolicyList(state: CustomerManagementModel) {
    return state.customerPolicyList ? state.customerPolicyList : null;
  }

  @Selector()
  public static getCustomerDetails(state: CustomerManagementModel) {
    return state.customerDetails ? state.customerDetails : null;
  }

  @Action(GET_CUSTOMER_MANAGEMENT_LOV)
  public getLov(
    { getState, patchState }: StateContext<CustomerManagementModel>,
    { payload }: GET_CUSTOMER_MANAGEMENT_LOV,
  ) {
    return this.cmService.getCustomerManagementLovV2(payload).pipe(
      map((res) => {
        if (res) {
          const currentLov = getState().lov ?? {};
          patchState({
            lov: {
              ...currentLov,
              ...(res.data ?? {}),
            },
          });
        }
      }),
    );
  }

  @Action(GET_ENQUIRY_LIST)
  public getEnquiryListing(
    { patchState }: StateContext<CustomerManagementModel>,
    { payload }: GET_ENQUIRY_LIST,
  ) {
    return this.cmService.getEnquiryListing(payload).pipe(
      map((res) => {
        if (res) {
          patchState({
            asOfDate: res.asOfDate,
            enquiryList: res.clients ? res.clients : [],
          });
        }
      }),
    );
  }

  @Action(GET_CUSTOMER_DETAILS)
  public getCustomerDetails(
    { patchState }: StateContext<CustomerManagementModel>,
    { payload }: GET_CUSTOMER_DETAILS,
  ) {
    return this.cmService.getCustomerDetails(payload).pipe(
      map((res) => {
        if (res) {
          patchState({
            customerDetails: res.data,
          });
        }
        if (res.error) {
          patchState({
            customerDetails: "error",
          });
        }
      }),
    );
  }

  @Action(SET_GWP_DATA)
  public setGWPData(
    { patchState }: StateContext<CustomerManagementModel>,
    { payload }: SET_GWP_DATA,
  ) {
    patchState({
      gwpData: payload,
    });
  }

  @Action(GET_ADMIN_CROSS_SELL_LIST)
  public getAdminCrossSellList(
    { patchState }: StateContext<CustomerManagementModel>,
    { payload }: GET_ADMIN_CROSS_SELL_LIST,
  ) {
    return this.cmService.getAdminCrossSellListing(payload).pipe(
      map((res) => {
        if (res) {
          patchState({
            adminCrossSellList: res.crossSells ? res.crossSells : null,
          });
        }
      }),
    );
  }

  @Action(ARCHIVE_CROSS_SELL)
  public archiveCrossSell(
    _ctx: StateContext<CustomerManagementState>,
    { payload }: ARCHIVE_CROSS_SELL,
  ) {
    return this.cmService.archiveCrossSell(payload).pipe(map(() => {}));
  }

  @Action(CREATE_CROSS_SELL)
  public createCrossSell(
    { patchState }: StateContext<CustomerManagementModel>,
    { payload }: CREATE_CROSS_SELL,
  ) {
    return Observable.create((observable) => {
      const formData = new FormData();
      if (payload.file) {
        formData.append("bannerImg", payload.file);
      }
      formData.append("crossSell", JSON.stringify(payload.detail));
      const xhr = new XMLHttpRequest();

      xhr.onreadystatechange = (_e) => {
        if (xhr.readyState !== 4) {
          return;
        }
        if (xhr.status === 200) {
          const data = JSON.parse(xhr.response) as CrossSellMutationResponse;
          console.log("data: ", data);
          const isSuccess = this.isCreateCrossSellSuccess(xhr.status, data);

          observable.next();

          patchState({
            createCrossSellStatus: {
              isSuccess,
              errorMsg: isSuccess
                ? null
                : (data?.error ?? data?.message ?? null),
            },
          });
        } else {
          this.checkingErrorCode(xhr);
        }

        observable.complete();
      };
      xhr.open("POST", this.cmService.createCrossSell(), true);
      xhr.send(formData);
    });
  }

  @Action(UPDATE_CROSS_SELL)
  public updateCrossSell(
    { patchState }: StateContext<CustomerManagementModel>,
    { payload }: UPDATE_CROSS_SELL,
  ) {
    return Observable.create((observable) => {
      const formData = new FormData();
      if (payload.file) {
        formData.append("bannerImg", payload.file);
      }
      formData.append("crossSell", JSON.stringify(payload.detail));
      const xhr = new XMLHttpRequest();

      xhr.onreadystatechange = (_e) => {
        if (xhr.readyState !== 4) {
          return;
        }
        if (xhr.status === 200) {
          const data = JSON.parse(xhr.response) as CrossSellMutationResponse;
          console.log("data: ", data);
          const isSuccess = this.isUpdateCrossSellSuccess(xhr.status, data);

          observable.next();

          patchState({
            updateCrossSellStatus: {
              isSuccess,
              errorMsg: isSuccess
                ? null
                : (data?.error ?? data?.message ?? null),
            },
          });
        } else {
          this.checkingErrorCode(xhr);
        }

        observable.complete();
      };
      xhr.open(
        "PUT",
        this.cmService.editCrossSell(payload.detail.crossSellId),
        true,
      );
      xhr.send(formData);
    });
  }

  @Action(SET_UPDATE_CROSS_SELL_DETAILS)
  public setCrossSellDetails(
    { patchState }: StateContext<CustomerManagementModel>,
    { payload }: SET_UPDATE_CROSS_SELL_DETAILS,
  ) {
    return patchState({
      updateCrossSell: payload,
    });
  }

  @Action(GET_CROSS_SELL_ENQUIRY)
  public getCrossSellEnquiryList(
    { patchState }: StateContext<CustomerManagementModel>,
    { payload }: GET_CROSS_SELL_ENQUIRY,
  ) {
    return this.cmService.getCrossSellEnquiry(payload).pipe(
      map((res) => {
        if (res) {
          patchState({
            crossSellEnquiryList: res ? res : null,
          });
        }
      }),
    );
  }

  @Action(DOWNLOAD_REPORT)
  public downloadReport(
    { patchState }: StateContext<CustomerManagementModel>,
    { payload }: DOWNLOAD_REPORT,
  ) {
    return this.cmService.downloadReport(payload).pipe(
      map((res) => {
        patchState({
          crossSellReport: null,
          fileName: null,
          statusCode: null,
        });
        if (String(res.status) === "202") {
          patchState({
            crossSellReport: res,
            statusCode: "202",
          });
        } else if (String(res.status) === "200") {
          console.log("res header: ", res.headers.get("content-disposition"));
          patchState({
            fileName: res.headers.get("content-disposition"),
            crossSellReport: res,
          });
        } else {
          patchState({
            crossSellReport: null,
          });
        }
      }),
    );
  }

  @Action(PATCH_AS_OF_DATE)
  public patchAsOfDate(
    { patchState }: StateContext<CustomerManagementModel>,
    { payload }: PATCH_AS_OF_DATE,
  ) {
    return patchState({
      asOfDate: payload,
    });
  }

  @Action(GET_COMPANY_PROFILE_FOLDER)
  public getCompanyProfileFolder(
    { patchState }: StateContext<CustomerManagementModel>,
    { payload }: GET_COMPANY_PROFILE_FOLDER,
  ) {
    return this.cmService.getStructuredFolder(payload).pipe(
      map((res) => {
        if (res) {
          patchState({
            companyProfileFolder: res,
          });
        }
      }),
    );
  }

  @Action(GET_FORMS_FOLDER)
  public getFormsFolder(
    { patchState }: StateContext<CustomerManagementModel>,
    { payload }: GET_FORMS_FOLDER,
  ) {
    return this.cmService.getStructuredFolder(payload).pipe(
      map((res) => {
        if (res) {
          patchState({
            formsFolder: res,
          });
        }
      }),
    );
  }

  @Action(GET_GUIDES_FOLDER)
  public getGuidesFolder(
    { patchState }: StateContext<CustomerManagementModel>,
    { payload }: GET_GUIDES_FOLDER,
  ) {
    return this.cmService.getStructuredFolder(payload).pipe(
      map((res) => {
        if (res) {
          patchState({
            guidesFolder: res,
          });
        }
      }),
    );
  }

  @Action(COMPILE_GET_PDF_FOLDER)
  public compileGetPdfFolder({
    patchState,
  }: StateContext<CustomerManagementModel>) {
    const companyProfileFolder = this.store.selectSnapshot(
      (state) => state.CustomerManagementState.companyProfileFolder.folders,
    );
    const formsFolder = this.store.selectSnapshot(
      (state) => state.CustomerManagementState.formsFolder.folders,
    );
    const guidesFolder = this.store.selectSnapshot(
      (state) => state.CustomerManagementState.guidesFolder.folders,
    );

    const compiledArray = [];

    for (const companyProfile of companyProfileFolder) {
      compiledArray.push(companyProfile);
    }

    for (const form of formsFolder) {
      compiledArray.push(form);
    }

    for (const guide of guidesFolder) {
      compiledArray.push(guide);
    }

    const pdfArray = [];

    for (const folder of compiledArray) {
      for (const content of folder.contents) {
        const fileExtension =
          content.contentFields[0].contentFieldValue.document.fileExtension;
        if (fileExtension === "pdf") {
          pdfArray.push(content);
        }
      }
    }

    patchState({
      folderContents: pdfArray,
    });

    return;
  }

  @Action(SAVE_EMAIL_SENT_LOG)
  public saveEmailLog(
    _ctx: StateContext<CustomerManagementModel>,
    { payload }: SAVE_EMAIL_SENT_LOG,
  ) {
    return this.cmService.saveEmailLog(payload);
  }

  @Action(SAVE_MESSAGE_SENT_LOG)
  public saveMessageLog(
    _ctx: StateContext<CustomerManagementModel>,
    { payload }: SAVE_MESSAGE_SENT_LOG,
  ) {
    return this.cmService.saveMessageLog(payload);
  }

  @Action(GET_CUSTOMER_POLICY_LIST)
  public getCustomerPolicyList(
    { patchState }: StateContext<CustomerManagementModel>,
    { payload }: GET_CUSTOMER_POLICY_LIST,
  ) {
    return this.cmService.getCustomerPolicyList(payload).pipe(
      map((res) => {
        if (res) {
          patchState({
            customerPolicyList: res,
          });
        }
      }),
    );
  }

  @Action(GET_GWP_DATA)
  public getGwpData(
    { patchState }: StateContext<CustomerManagementModel>,
    { payload }: GET_GWP_DATA,
  ) {
    return this.cmService.getGwpData(payload).pipe(
      map((res) => {
        if (res) {
          patchState({
            gwpData: {
              motorGwp: res.gwpMT,
              nonMotorGwp: res.gwpNMT,
            },
          });
        }
      }),
    );
  }

  @Action(SET_POLICY_REQUEST)
  public setPolicyRequest(
    { patchState }: StateContext<CustomerManagementModel>,
    { payload }: SET_POLICY_REQUEST,
  ) {
    return patchState({
      policyRequest: payload,
    });
  }

  @Action(GET_POSTCODE_DETAILS)
  public getPostcodeDetails(
    { patchState }: StateContext<CustomerManagementModel>,
    { payload }: GET_POSTCODE_DETAILS,
  ) {
    return this.cmService.getPostcodeDetails(payload).pipe(
      map((res) => {
        if (res.message) {
          patchState({
            postcodeDetails: {
              code: "",
              descp: "",
              city: "",
              state: "",
            },
          });
        } else {
          patchState({
            postcodeDetails: res.data,
          });
        }
      }),
    );
  }

  @Action(UPDATE_CUSTOMER_DETAILS)
  public updateCustomerDetails(
    { patchState }: StateContext<CustomerManagementModel>,
    { payload }: UPDATE_CUSTOMER_DETAILS,
  ) {
    patchState({
      updateCustomerStatus: null,
    });
    return this.cmService.updateCustomerDetails(payload).pipe(
      map((res) => {
        if (res.success) {
          patchState({
            customerDetails: res.data,
            updateCustomerStatus: {
              isSuccess: res.success,
              errorMsg: null,
            },
          });
        } else {
          patchState({
            updateCustomerStatus: {
              isSuccess: false,
              errorMsg: res.message ? res.message : "",
            },
          });
        }
      }),
    );
  }

  @Action(GET_CUSTOMER_ENDORSEMENT_HISTORY)
  public getCustomerEndtHistory(
    { patchState }: StateContext<CustomerManagementModel>,
    { payload }: GET_CUSTOMER_ENDORSEMENT_HISTORY,
  ) {
    return this.cmService.getCustomerEndtHistory(payload).pipe(
      map((res) => {
        if (res) {
          patchState({
            customerEndorsementHistory: res,
          });
        }
      }),
    );
  }

  @Action(GET_CUSTOMER_POLICY_DETAILS)
  public getCustomerPolicyDetails(
    { patchState }: StateContext<CustomerManagementModel>,
    { payload }: GET_CUSTOMER_POLICY_DETAILS,
  ) {
    return this.cmService.getCustomerPolicyDetails(payload).pipe(
      map((res) => {
        if (res) {
          console.log("response: ", res);

          patchState({
            fileName: res.headers.get("content-disposition"),
            customerPolicyDetails: res,
          });
        }
      }),
    );
  }

  @Action(GET_CUSTOMER_CLAIM_ENQUIRY)
  public getCustomerClaimEnquiry(
    { patchState }: StateContext<CustomerManagementModel>,
    { payload }: GET_CUSTOMER_POLICY_DETAILS,
  ) {
    return this.cmService.getCustomerClaimEnquiry(payload).pipe(
      map((res) => {
        if (res) {
          patchState({
            customerClaimDetails: res,
          });
        }
      }),
    );
  }

  @Action(GET_CUSTOMER_INTERACTION_DATA)
  public getCustomerInteractionData(
    { patchState }: StateContext<CustomerManagementModel>,
    { payload }: GET_CUSTOMER_INTERACTION_DATA,
  ) {
    return this.cmService.getCustomerInteractionData(payload).pipe(
      map((res) => {
        if (res) {
          patchState({
            customerInteractionData: {
              disabledLoading: true,
              data: res,
            },
          });
        }
      }),
    );
  }

  @Action(SET_MAIN_AGENT)
  public setMainAgent(
    { patchState }: StateContext<CustomerManagementModel>,
    { payload }: SET_MAIN_AGENT,
  ) {
    return patchState({
      mainAgentInd: payload,
    });
  }

  @Action(SET_ENQUIRY_SEARCH_CRITERIA)
  public setEnquirySearchCriteria(
    { patchState }: StateContext<CustomerManagementModel>,
    { payload }: SET_ENQUIRY_SEARCH_CRITERIA,
  ) {
    return patchState({
      enquirySearchCriteria: payload,
    });
  }

  @Action(GET_REDIRECT_URL)
  public getRedirectUrl(
    _ctx: StateContext<CustomerManagementModel>,
    { payload }: GET_REDIRECT_URL,
  ) {
    return this.cmService.getRedirectUrl(payload).pipe(
      map((res) => {
        if (res.url) {
          window.location.href = res.url;
        }
      }),
    );
  }

  @Action(GET_PRODUCT_PDF)
  public getPdfProducts(
    { patchState }: StateContext<CustomerManagementModel>,
    { payload }: GET_PRODUCT_PDF,
  ) {
    return this.cmService.getStructuredFolder(payload).pipe(
      map((res) => {
        if (res) {
          const pdfArray = [];

          for (const folder of res.folders) {
            for (const folder2 of folder.folders) {
              for (const folder3 of folder2.folders) {
                for (const folder4 of folder3.folders) {
                  for (const folder5 of folder4.folders) {
                    if (!folder5.contents) {
                      continue;
                    }
                    for (const content of folder5.contents) {
                      if (
                        content.contentFields[0].contentFieldValue.document.fileExtension.toLowerCase() ===
                        "pdf"
                      ) {
                        pdfArray.push(content);
                      }
                    }
                  }
                }
              }
            }
          }

          patchState({
            folderContents: pdfArray,
          });
        }
      }),
    );
  }

  @Action(CLEAR_PRODUCT_PDF)
  public clearPdfProducts({
    patchState,
  }: StateContext<CustomerManagementModel>) {
    patchState({
      folderContents: [],
    });
  }

  @Action(RESET_INTERACTION_DATA)
  public resetInteractionData(
    { patchState }: StateContext<CustomerManagementModel>,
    _action: RESET_INTERACTION_DATA,
  ) {
    patchState({
      customerInteractionData: null,
    });
  }

  @Action(GET_ALPHA_FLAG)
  public getAlphaFlag(
    { patchState }: StateContext<CustomerManagementModel>,
    { payload }: GET_ALPHA_FLAG,
  ) {
    return this.cmService.getAlphaProductFlag(payload).pipe(
      map((res) => {
        if (res) {
          patchState({
            isAlphaProduct: res,
          });
        }
      }),
    );
  }

  @Action(GET_ALPHA_ROUTING_URL)
  public getAlphaRoutingUrl(
    _ctx: StateContext<CustomerManagementModel>,
    { payload }: GET_ALPHA_ROUTING_URL,
  ) {
    return this.cmService.getAlphaRoutingURL(payload).pipe(
      map((res) => {
        if (res.redirectUrl) {
          window.location.href = res.redirectUrl;
        }
      }),
    );
  }

  @Action(GET_COVER_NOTE_VALIDATION)
  public getCoverNoteValidation(
    { patchState }: StateContext<CustomerManagementModel>,
    { payload }: GET_COVER_NOTE_VALIDATION,
  ) {
    patchState({
      coverNoteValidation: null,
    });
    return this.cmService.getCoverNoteValidation(payload).pipe(
      map((res) => {
        if (res) {
          patchState({
            coverNoteValidation: res,
          });
        }
      }),
    );
  }

  @Action(GET_POLICY_VALIDATION)
  public getPolicyValidation(
    { patchState }: StateContext<CustomerManagementModel>,
    { payload }: GET_POLICY_VALIDATION,
  ) {
    patchState({
      policyValidation: null,
    });
    return this.cmService.getPolicyValidation(payload).pipe(
      map((res) => {
        if (res) {
          patchState({
            policyValidation: res,
          });
        }
      }),
    );
  }

  @Action(GET_ALIM_CP_DETAILS)
  public setAlimCPInd(
    { patchState }: StateContext<CustomerManagementModel>,
    { payload }: GET_ALIM_CP_DETAILS,
  ) {
    return this.cmService.getAlimCPDetails(payload).pipe(
      map((res) => {
        const alimInd = res?.detail?.alimCustomerIndicator === "Y";
        patchState({
          alimCustomerDetails: {
            alimCustomerInd: alimInd,
            message: res?.detail?.errorMsg,
            isError: !!res?.errror,
          },
        });
      }),
    );
  }

  public checkingErrorCode(res) {
    const data = JSON.parse(res.response);
    console.log("response: ", data);
    console.log("xhr response: ", res);

    const errorCodesList1 = [401, 403, 302, "401", "403", "302"];
    const errorCodesList2 = [500, 503, "500", "503"];

    if (res.status === 400) {
      this.store.dispatch(new SET_CONCURRENT_LOGIN_ERROR(data));
    }
    if (errorCodesList1.includes(res.status)) {
      if (data.isForcedLogout != null && data.isForcedLogout) {
        this.store.dispatch(new SET_CONCURRENT_LOGIN_ERROR(data));
      } else {
        this.store.dispatch(new NAVIGATE_LOGIN_PAGE());
      }
    }
    if (errorCodesList2.includes(res.status)) {
      this.store.dispatch(new PROMPT_ERROR(data));
    }
  }

  private hasCrossSellFailureSignal(data: CrossSellMutationResponse): boolean {
    const hasErrorField = data?.error !== null && data?.error !== undefined;
    const hasMessageField =
      typeof data?.message === "string" && data.message.trim().length > 0;

    return hasErrorField || hasMessageField;
  }

  private isCreateCrossSellSuccess(
    httpStatus: number,
    data: CrossSellMutationResponse,
  ): boolean {
    const hasHttp200 = httpStatus === 200;
    const hasExplicitSuccessFlag = data?.success === true;
    const hasLegacyBooleanStatusSuccess = data?.status === true;
    const hasActiveStatus = data?.status === "ACTIVE";
    const hasValidCrossSellId =
      typeof data?.crossSellId === "number" &&
      Number.isInteger(data.crossSellId) &&
      data.crossSellId > 0;
    const hasExplicitFailure = this.hasCrossSellFailureSignal(data);

    return (
      hasHttp200 &&
      !hasExplicitFailure &&
      (hasExplicitSuccessFlag ||
        hasLegacyBooleanStatusSuccess ||
        hasActiveStatus ||
        hasValidCrossSellId)
    );
  }

  private isUpdateCrossSellSuccess(
    httpStatus: number,
    data: CrossSellMutationResponse,
  ): boolean {
    const hasHttp200 = httpStatus === 200;
    const hasExplicitSuccessFlag = data?.success === true;
    const hasLegacyBooleanStatusSuccess = data?.status === true;
    const hasActiveStatus = data?.status === "ACTIVE";
    const hasExplicitFailure = this.hasCrossSellFailureSignal(data);

    return (
      hasHttp200 &&
      !hasExplicitFailure &&
      (hasExplicitSuccessFlag ||
        hasLegacyBooleanStatusSuccess ||
        hasActiveStatus)
    );
  }
}
